import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box
} from '@mui/material';
import dayjs from 'dayjs';
import { 
  getRateForRole, 
  getProjectYear,
  validateAnnualWorkingDays,
  determineRateType
} from '../../../utils/rateUtils';
import { roleToFunctionMap, roleToFocusMap } from '../../../utils/wizardHelpers';

const defaultRoles = ["Product", "Tech Lead", "BA", "UX", "FE", "BE", "QA", "Other"];

const AddRoleDialog = ({
  open,
  onClose,
  milestone,
  onAddRole,
  locationOptions,
  roleOptions,
  outcomeData
}) => {
  const [roleForm, setRoleForm] = useState({
    name: 'Product',
    company: 'CA',
    location: 'US',
    function: 'Product Manager',
    role: '',
    rate: 0,
    rateAdjustmentInfo: null
  });

  const handleFormChange = useCallback(async (field, value) => {
    setRoleForm(prev => {
      let updated = { ...prev, [field]: value };

      if (field === 'name') {
        updated.function = roleToFunctionMap[value] || "Other";
      }

      if (field === 'company') {
        const availableLocations = locationOptions[value] || [];
        updated.location = availableLocations.length > 0 ? availableLocations[0].code : 'US';
        updated.role = '';
      }

      if (field === 'location') {
        updated.role = '';
      }

      // Update rate when location/role changes
      if (['company', 'location', 'role'].includes(field)) {
        const projectYear = getProjectYear(outcomeData, null, new Date().getFullYear());
        getRateForRole(
          updated.company, 
          updated.location, 
          field === 'role' ? value : updated.role, 
          projectYear
        ).then(rateInfo => {
          if (rateInfo.monthlyRate > 0) {
            setRoleForm(current => ({
              ...current,
              rate: rateInfo.monthlyRate,
              rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
            }));
          }
        }).catch(error => {
          console.warn('Could not get COLA-adjusted rate:', error);
        });
      }

      return updated;
    });
  }, [locationOptions, roleOptions, outcomeData]);

  const handleAdd = useCallback(() => {
    if (!milestone) return;

    const mappedFocus = roleForm.function === "Developer" ? (roleToFocusMap[roleForm.name] || "Fullstack") : undefined;
    const milestoneStart = dayjs(milestone.start_date);
    const milestoneEnd = dayjs(milestone.end_date);

    // Use direct calendar months (1-12)
    const milestoneStartCalendarMonth = milestoneStart.month() + 1;
    const milestoneEndCalendarMonth = milestoneEnd.month() + 1;
    const milestoneDuration = Math.max(1, milestoneEnd.diff(milestoneStart, 'month') + 1);

    const defaultWorkingDays = roleForm.company === 'CA' ? 252 : 260;
    const roleWorkingDays = validateAnnualWorkingDays(defaultWorkingDays, `new role ${roleForm.name}`);

    const newRole = {
      id: `${milestone.id}-${roleForm.name}-${Date.now()}`,
      milestone_id: milestone.id,
      name: roleForm.name,
      company: roleForm.company,
      location: roleForm.location,
      function: roleForm.function,
      focus: mappedFocus,
      role: roleForm.role,
      rate: roleForm.rate,
      roster_member_id: "",
      notes: "",
      allocation: 100,
      capex_classification: milestone.capex_classification || 'OPEX',
      capex_source: 'milestone',
      original_role_id: `${roleForm.name}-${Date.now()}`,
      milestone_start_month: milestoneStartCalendarMonth,
      milestone_end_month: milestoneEndCalendarMonth,
      overlap_months: milestoneDuration,
      useDefaultTimeline: true,
      defaultAllocation: 100,
      dateRanges: [{
        startMonth: milestoneStartCalendarMonth,
        endMonth: milestoneEndCalendarMonth,
        allocation: 100,
        timelineVersion: '3.0'
      }],
      rateAdjustmentInfo: roleForm.rateAdjustmentInfo,
      lastRateUpdate: new Date().toISOString(),
      timelineVersion: '2.0',
      annualWorkingDays: roleWorkingDays,
      effectiveAnnualWorkingDays: roleWorkingDays,
      rateType: determineRateType(roleWorkingDays)
    };

    onAddRole(newRole);
  }, [milestone, roleForm, onAddRole]);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open && milestone) {
      const projectYear = getProjectYear(outcomeData, null, new Date().getFullYear());
      getRateForRole('CA', 'US', '', projectYear)
        .then(rateInfo => {
          setRoleForm({
            name: 'Product',
            company: 'CA',
            location: 'US',
            function: 'Product Manager',
            role: '',
            rate: rateInfo.monthlyRate || 13583,
            rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
          });
        })
        .catch(error => {
          console.warn('Could not get COLA-adjusted rate for new role:', error);
          setRoleForm({
            name: 'Product',
            company: 'CA',
            location: 'US',
            function: 'Product Manager',
            role: '',
            rate: 13583,
            rateAdjustmentInfo: null
          });
        });
    }
  }, [open, milestone, outcomeData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add Role to {milestone?.name}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role Name</InputLabel>
              <Select
                value={roleForm.name}
                label="Role Name"
                onChange={(e) => handleFormChange('name', e.target.value)}
              >
                {defaultRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={roleForm.company}
                label="Company"
                onChange={(e) => handleFormChange('company', e.target.value)}
              >
                {Object.keys(locationOptions).map(company => (
                  <MenuItem key={company} value={company}>{company}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={roleForm.location}
                label="Location"
                onChange={(e) => handleFormChange('location', e.target.value)}
              >
                {(locationOptions[roleForm.company] || []).map(location => (
                  <MenuItem key={location.code} value={location.code}>{location.display}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Specific Role</InputLabel>
              <Select
                value={roleForm.role}
                label="Specific Role"
                onChange={(e) => handleFormChange('role', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a role...</em>
                </MenuItem>
                {(roleOptions[roleForm.company]?.[roleForm.location] || []).map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Monthly Rate"
              type="number"
              value={roleForm.rate}
              onChange={(e) => handleFormChange('rate', Number(e.target.value))}
              InputProps={{
                startAdornment: '$'
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Function: {roleForm.function}
            </Typography>
            {roleForm.rateAdjustmentInfo && (
              <Typography variant="caption" color="warning.main">
                Rate includes COLA adjustment
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained">Add Role</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRoleDialog;